import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Braces,
  Check,
  Component as ComponentIcon,
  Copy,
  Info,
  LayoutTemplate,
  Lock,
  Plus,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  FOOTER_SYSTEM_VARIABLE_NAMES,
  FOOTER_SYSTEM_VARIABLES,
  buildStarterFooterBlocks,
  footerReferencesUnsubscribe,
  parseEmailFooterConfig,
  type Block,
  type EmailFooterConfig,
  type FooterSystemVariable,
} from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import { useEditorHistory, useEditorStore, type BlockDraft } from '../stores/editorStore.js';
import { EditorToolbar } from '../components/editor/EditorToolbar.js';
import { EditorCanvas } from '../components/editor/EditorCanvas.js';
import { InspectorPanel } from '../components/InspectorPanel.js';
import { PanelResizer } from '../components/PanelResizer.js';
import { useResizablePanel } from '../hooks/use-resizable-panel.js';
import { useEditorShortcuts } from '../hooks/useEditorShortcuts.js';
import { PaneLoading } from '../components/PaneLoading.js';

// ─────────────────────────────────────────────────────────────────
// Workspace email footer editor
// ─────────────────────────────────────────────────────────────────
// The footer is content now: a block list stored on site settings and rendered
// by the theme through the ordinary email block renderers. So this page is the
// same block editor as the component designer (ComponentEditorPage) — same
// canvas kit, same zustand store, same inspector — run in its own store scope
// ('footer-workspace') so the document it edits can never be confused with a
// post, an email or a component.
//
// What it adds on top of the bare canvas:
//
//   · the system variables the footer may reference, offered both as a palette
//     and inline in text blocks (typing `@`), reading live site data instead of
//     freezing today's values into text;
//   · the system footer template and the workspace's saved email components,
//     as starting points;
//   · an unsubscribe check, because the build refuses a custom footer without
//     an opt-out link — far better to see that here than at send time.
// ─────────────────────────────────────────────────────────────────

/** Editor-store scope: one per document kind, so no editor inherits another's state. */
const FOOTER_SCOPE = 'footer-workspace';

type ToolPanel = 'none' | 'variables' | 'components';

/** The slice of GET /api/settings/site this page reads. */
interface SiteSettingsResponse {
  siteName?: string;
  /** Normalised footer config (an object, never the JSON string an old row held). */
  emailFooter?: unknown;
}

interface ComponentRow {
  id: string;
  name: string;
  description: string | null;
  target: string;
  /** JSON-encoded Block[]. */
  blocks: string;
  version: number;
  /** System templates: insertable, never editable. */
  locked?: boolean;
}

/** The toolbar title is a plain label here — a footer has no name of its own. */
const noop = () => undefined;

/**
 * Read the footer config the settings route returned.
 *
 * The route normalises the column to an object, but an unreadable value would
 * otherwise be spread into the save payload and rejected — or worse, silently
 * turn the authored content into junk.
 */
function readFooter(raw: unknown): EmailFooterConfig | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') return parseEmailFooterConfig(raw);
  return raw as EmailFooterConfig;
}

/**
 * A footer text line — the same shape buildStarterFooterBlocks emits, with the
 * flag the renderers key off so a `{{token}}` in it is resolved.
 */
function textLine(content: string): BlockDraft {
  return {
    type: 'paragraph',
    content: `<p>${content}</p>`,
    contentHasVar: content.includes('{{'),
    attrs: {},
    style: { fontSize: '13px', textAlign: 'center' },
  } as BlockDraft;
}

/** Terminal states of the page's own load — never a spinner that never ends. */
function FooterNotice({
  title,
  body,
  onBack,
}: {
  title: string;
  body: string;
  onBack: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-ink text-sm font-semibold">{title}</p>
      <p className="text-muted max-w-md text-xs">{body}</p>
      <button
        type="button"
        onClick={onBack}
        className="btn-ghost mt-1 rounded-lg px-3 py-1.5 text-xs"
      >
        Back to settings
      </button>
    </div>
  );
}

/** One variable row: what it is, and the two ways to get it into the footer. */
function VariablePalette({
  copiedToken,
  onInsert,
  onCopy,
  onClose,
}: {
  copiedToken: string | null;
  onInsert: (variable: FooterSystemVariable) => void;
  onCopy: (variable: FooterSystemVariable) => void;
  onClose: () => void;
}) {
  return (
    <div className="border-line bg-bg-soft/60 max-h-80 shrink-0 overflow-y-auto border-b px-4 py-3">
      <div className="mb-1 flex items-start justify-between gap-3">
        <p className="kicker">
          <span className="bg-accent h-1.5 w-1.5 rounded-full" />
          System variables
        </p>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          className="text-muted hover:bg-ink/5 hover:text-ink flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
        >
          <X size={13} />
        </button>
      </div>
      <p className="text-muted mb-3 text-xs">
        These read live workspace data every time an email is built. In any paragraph, heading or
        quote you can also type <code className="font-mono">@</code> and pick one — or add the token
        to a link, image URL, button or HTML block by hand.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {FOOTER_SYSTEM_VARIABLES.map((variable) => (
          <div
            key={variable.name}
            className="border-line bg-card flex items-start gap-2.5 rounded-xl border p-2.5"
          >
            <span className="bg-accent/10 text-accent flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
              <Braces size={13} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-baseline gap-1.5">
                <span className="text-ink text-xs font-semibold">{variable.label}</span>
                <code className="text-accent font-mono text-[11px]">{`{{${variable.name}}}`}</code>
              </p>
              <p className="text-muted mt-0.5 text-[11px] leading-snug">{variable.description}</p>
              {variable.html && (
                <p className="text-warn mt-1 text-[10px] font-semibold tracking-[0.08em] uppercase">
                  Markup — use it on its own line
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onInsert(variable)}
                  className="btn-ghost h-6 gap-1 rounded-md px-2 text-[11px]"
                >
                  <Plus size={11} />
                  Add as a line
                </button>
                <button
                  type="button"
                  onClick={() => onCopy(variable)}
                  className="btn-ghost h-6 gap-1 rounded-md px-2 text-[11px]"
                >
                  {copiedToken === variable.name ? <Check size={11} /> : <Copy size={11} />}
                  {copiedToken === variable.name ? 'Copied' : 'Copy token'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Saved email components — inserted as a copy of their blocks. */
function ComponentPicker({
  components,
  loading,
  onInsert,
  onClose,
}: {
  components: ComponentRow[];
  loading: boolean;
  onInsert: (component: ComponentRow) => void;
  onClose: () => void;
}) {
  const countBlocks = (component: ComponentRow): number => {
    try {
      const parsed = JSON.parse(component.blocks) as unknown;
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  };

  return (
    <div className="border-line bg-bg-soft/60 max-h-80 shrink-0 overflow-y-auto border-b px-4 py-3">
      <div className="mb-1 flex items-start justify-between gap-3">
        <p className="kicker">
          <span className="bg-accent h-1.5 w-1.5 rounded-full" />
          Saved components for email
        </p>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          className="text-muted hover:bg-ink/5 hover:text-ink flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors"
        >
          <X size={13} />
        </button>
      </div>
      <p className="text-muted mb-3 text-xs">
        Inserting copies the component's blocks into the footer, so the copy is yours to edit —
        including the system footer, which cannot be edited in the component library.
      </p>

      {loading ? (
        <p className="text-muted text-xs">Loading components…</p>
      ) : components.length === 0 ? (
        <p className="border-line text-muted rounded-xl border border-dashed px-3 py-3 text-xs">
          No email components yet — save one from the email editor to reuse it here.
        </p>
      ) : (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {components.map((component) => (
            <button
              type="button"
              key={component.id}
              onClick={() => onInsert(component)}
              className="group border-line bg-card hover:border-accent-border flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all duration-150 hover:-translate-y-px hover:shadow-sm"
            >
              <span className="bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-ink flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors">
                {component.locked ? <Lock size={12} /> : <ComponentIcon size={13} />}
              </span>
              <span className="text-ink-soft group-hover:text-ink min-w-0 flex-1 truncate text-xs font-semibold transition-colors">
                {component.name}
              </span>
              <span className="text-muted hidden flex-none text-[10px] sm:block">
                {countBlocks(component)} block{countBlocks(component) === 1 ? '' : 's'}
              </span>
              {component.locked && (
                <span className="chip shrink-0 px-2 py-0.5 text-[9.5px] tracking-wide uppercase">
                  System
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Footer designer — author the workspace's default email footer as blocks.
 *
 * Saving PUTs the *complete* footer config (details included): the route
 * replaces the footer wholesale, so carrying the tagline/logo/socials over
 * untouched is what keeps `{{tagline}}` and friends working.
 */
/** The email row fields this page needs (email scope). */
interface EmailWithFooter {
  footerOverride?: { blocks?: Block[] } | null;
}

interface EmailFooterPageProps {
  /** 'workspace' (default) edits the shared footer; 'email' edits one email's. */
  scope?: 'workspace' | 'email';
}

export function EmailFooterPage({ scope = 'workspace' }: EmailFooterPageProps = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const inspector = useResizablePanel();
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const { id: emailId } = useParams<{ id: string }>();
  const isEmailScope = scope === 'email' && Boolean(emailId);

  /**
   * The footer being edited, and where it is saved:
   *
   *   workspace → siteSettings.emailFooter (PUT /api/settings/site), the
   *               default every email renders.
   *   email     → that email's `footerOverride` (PUT /api/emails/:id), which
   *               replaces the content for that one send.
   *
   * Both surfaces are the same editor; only the load/save pair differs.
   */
  const backPath = isEmailScope ? `/emails/${emailId}/edit` : '/settings';
  /** Key for "which footer is loaded into the store", so switching between
   *  workspaces/emails reloads instead of showing the previous one. */
  const footerScopeKey = isEmailScope ? `email:${emailId}` : `workspace:${activeWorkspaceId}`;

  const {
    blocks,
    selectedBlockId,
    setBlocks,
    setComponentUsages,
    setPostId,
    addBlock,
    applyDraftAppend,
    selectBlock,
  } = useEditorStore();
  const { undo, redo, canUndo, canRedo } = useEditorHistory();

  // The inspector is an overlay sheet on small screens (same wiring as the
  // post/email/component editors: the shell styles it off body.inspector-open).
  useEffect(() => {
    document.body.classList.toggle('inspector-open', !inspector.collapsed);
    return () => document.body.classList.remove('inspector-open');
  }, [inspector.collapsed]);

  const [tool, setTool] = useState<ToolPanel>('none');
  /** The footer as loaded — the source of the details the variables read. */
  const [details, setDetails] = useState<EmailFooterConfig | null>(null);
  /** Workspace whose footer is currently loaded into the store (null = none yet). */
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const initialBlocksJson = useRef<string | null>(null);
  const savedFlashTimer = useRef<ReturnType<typeof setTimeout>>();
  const copyTimer = useRef<ReturnType<typeof setTimeout>>();

  // Same key as the settings page: the footer belongs to the workspace, and a
  // save over there must not leave a stale footer loaded here.
  const { data, isError, error } = useQuery({
    queryKey: ['site-settings', activeWorkspaceId],
    queryFn: () => api.settings.getSite(),
    enabled: Boolean(activeWorkspaceId),
  });

  // Email scope: the row carries this email's override, if it has one.
  const emailQuery = useQuery({
    queryKey: ['email', emailId],
    queryFn: () => api.emails.get(emailId!),
    enabled: isEmailScope,
  });
  const emailStatus = (emailQuery.data as { status?: string } | undefined)?.status;

  const componentsQuery = useQuery({
    queryKey: ['components', { target: 'email', limit: 100 }],
    queryFn: () => api.components.list({ limit: 100, target: 'email' }),
    enabled: tool === 'components',
  });
  const components: ComponentRow[] =
    (componentsQuery.data as { data?: ComponentRow[] } | undefined)?.data ?? [];

  // Load the workspace footer into the shared store exactly once per workspace
  // — a refetch (this page's own save, or the settings page saving details)
  // must not overwrite blocks the user is editing right now.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    if (data === undefined) return;
    // Email scope also needs the email row itself, or the first render would
    // load the workspace footer over an existing override.
    if (isEmailScope && emailQuery.data === undefined) return;
    if (loadedScope === footerScopeKey) return;

    const footer = readFooter((data as SiteSettingsResponse).emailFooter);
    const override = isEmailScope
      ? ((emailQuery.data as EmailWithFooter | undefined)?.footerOverride?.blocks ?? null)
      : null;
    const initial = override?.length ? override : (footer?.blocks ?? []);

    // A per-email scope of its own: two emails open in turn must not inherit
    // each other's undo history or selection.
    setPostId(isEmailScope ? `${FOOTER_SCOPE}-${emailId}` : FOOTER_SCOPE);
    setComponentUsages([]);
    setBlocks(initial);
    selectBlock(null);
    initialBlocksJson.current = JSON.stringify(initial);
    setDetails(footer);
    setLoadedScope(footerScopeKey);
  }, [
    activeWorkspaceId,
    data,
    emailQuery.data,
    isEmailScope,
    emailId,
    footerScopeKey,
    loadedScope,
    setBlocks,
    setComponentUsages,
    setPostId,
    selectBlock,
  ]);

  // A scope key for the canvas kit is not enough on its own: reopening the page
  // for another workspace must not show the previous footer for a frame.
  const ready = Boolean(activeWorkspaceId) && loadedScope === footerScopeKey;

  const blocksJson = JSON.stringify(blocks);
  const dirty = initialBlocksJson.current !== null && blocksJson !== initialBlocksJson.current;

  const saveMutation = useMutation({
    mutationFn: () =>
      isEmailScope
        ? api.emails.update(emailId!, { footerOverride: { blocks } })
        : api.settings.updateSite({
            // Wholesale replace on the server — send the whole footer, details
            // first (read, changed here only for `blocks`).
            emailFooter: { ...(details ?? {}), blocks } as EmailFooterConfig,
          }),
    onSuccess: () => {
      initialBlocksJson.current = JSON.stringify(blocks);
      void qc.invalidateQueries({ queryKey: ['site-settings'] });
      if (isEmailScope) void qc.invalidateQueries({ queryKey: ['email', emailId] });
      setSavedFlash(true);
      clearTimeout(savedFlashTimer.current);
      savedFlashTimer.current = setTimeout(() => setSavedFlash(false), 2000);
    },
  });

  useEditorShortcuts({
    onSave: () => {
      if (ready && dirty && !saveMutation.isPending) saveMutation.mutate();
    },
    undo,
    redo,
  });

  // The variables a footer may reference: the mention source the text blocks
  // read is derived from this record's *keys*, which is all the tiptap
  // suggestion needs to offer them from an `@` — and is also what makes a
  // text block set contentHasVar when a token lands in its content.
  const variableDefinitions = useMemo(
    () => Object.fromEntries(FOOTER_SYSTEM_VARIABLES.map((v) => [v.name, v.description])),
    [],
  );

  const [selectedId, selectedPath] = selectedBlockId ?? [null, null];
  // addBlock can only place a block next to a *root* block; a nested selection
  // (inside a section/columns) appends at the end of the footer instead.
  const anchorId = selectedId && selectedPath && selectedPath.length === 0 ? selectedId : undefined;

  const missingUnsubscribe = blocks.length > 0 && !footerReferencesUnsubscribe(blocks);

  const insertVariableLine = (variable: FooterSystemVariable) => {
    addBlock(textLine(`{{${variable.name}}}`), anchorId);
  };

  const copyToken = async (variable: FooterSystemVariable) => {
    const token = `{{${variable.name}}}`;
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      // Clipboard access can be denied; the token is on screen either way.
      return;
    }
    setCopiedToken(variable.name);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(
      () => setCopiedToken((current) => (current === variable.name ? null : current)),
      1600,
    );
  };

  const applyStarterTemplate = () => {
    if (
      blocks.length > 0 &&
      // eslint-disable-next-line no-restricted-globals
      !confirm(
        'Replace the footer content with the system footer template? Your current blocks are replaced (undo with ⌘Z).',
      )
    ) {
      return;
    }
    const starter = buildStarterFooterBlocks({ footer: details ?? {} });
    setBlocks(starter);
    selectBlock(starter.length > 0 ? [starter[0]!.id, []] : null);
    setTool('none');
  };

  const insertComponent = (component: ComponentRow) => {
    let parsed: unknown[] = [];
    try {
      parsed = JSON.parse(component.blocks) as unknown[];
    } catch {
      parsed = [];
    }
    if (!Array.isArray(parsed) || parsed.length === 0) return;
    // Cloned with fresh ids and appended — a copy, not a live reference (the
    // footer has no component-update flow, and a locked template must not be
    // edited in place through the back door).
    applyDraftAppend(parsed as Block[]);
    setTool('none');
  };

  const addUnsubscribeLine = () => {
    // An HTML block, not a paragraph: the canvas rewrites `{{token}}` into a
    // mention chip across a text block's whole markup — attributes included —
    // so a token inside a paragraph's link href would come back mangled. This
    // is the same line the system footer template ships (and the same reason
    // it uses an HTML block for it).
    addBlock(
      {
        type: 'html',
        content:
          '<p style="margin:0;font-size:13px;text-align:center;">' +
          '<a href="{{unsubscribe_url}}" style="color:inherit;text-decoration:underline;">Unsubscribe</a>' +
          '</p>',
        attrs: {},
        style: { fontSize: '13px', textAlign: 'center' },
      } as BlockDraft,
      anchorId,
    );
  };

  if (!ready) {
    // Nothing is editable before the workspace's footer is in the store —
    // otherwise a late load would land on top of the user's first blocks.
    if (!activeWorkspaceId) {
      return (
        <FooterNotice
          title="No workspace selected"
          body="Pick a workspace in the workspace switcher, then open the email footer again."
          onBack={() => navigate(backPath)}
        />
      );
    }
    if (isEmailScope && emailQuery.isError) {
      return (
        <FooterNotice
          title="The email could not be loaded"
          body="The email may have been deleted, or it belongs to another workspace."
          onBack={() => navigate('/emails')}
        />
      );
    }
    if (isError) {
      return (
        <FooterNotice
          title="The email footer could not be loaded"
          body={
            error instanceof Error
              ? `${error.message} — only workspace admins can change the footer.`
              : 'The workspace settings request failed.'
          }
          onBack={() => navigate(backPath)}
        />
      );
    }
    return <PaneLoading label="Loading email footer…" />;
  }

  // A sent email keeps the footer it was built with. The per-email panel hides
  // its controls for these too — this route is reachable by URL, so it has to
  // agree rather than quietly offering an edit that cannot reach anyone.
  if (isEmailScope && (emailStatus === 'sent' || emailStatus === 'sending')) {
    return (
      <FooterNotice
        title="This email has already been sent"
        body="It keeps the footer it was built with, so its footer can no longer be changed."
        onBack={() => navigate(backPath)}
      />
    );
  }

  return (
    <div
      className="editor-layout"
      style={{
        gridTemplateColumns: inspector.collapsed ? '1fr auto' : `1fr auto ${inspector.width}px`,
      }}
    >
      {/* ── Canvas side ─────────────────────────────────────── */}
      <div className="flex flex-col overflow-hidden">
        <EditorToolbar
          backTitle="settings"
          onBack={() => navigate(backPath)}
          onOpenInspector={() => inspector.setCollapsed(false)}
          title={{
            value: isEmailScope ? "This email's footer" : 'Email footer',
            onChange: noop,
            onCommit: noop,
            placeholder: 'Email footer',
          }}
          saveState={dirty ? 'dirty' : savedFlash ? 'saved' : 'idle'}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          onSave={() => saveMutation.mutate()}
          canSave={dirty}
          saving={saveMutation.isPending}
          primaryAction={
            <button
              type="button"
              onClick={() => navigate(isEmailScope ? backPath : '/settings')}
              className="btn-ghost h-8 shrink-0 gap-1.5 rounded-lg px-2.5 text-xs"
              title="Tagline, logo, socials, address and legal text — the data behind the variables"
            >
              {isEmailScope ? 'Back to email' : 'Footer details'}
            </button>
          }
        />

        {/* ── Authoring tools ──────────────────────────────── */}
        <div className="border-line bg-bg-soft/60 flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
          <p className="kicker">
            <span className="bg-accent h-1.5 w-1.5 rounded-full" />
            {isEmailScope ? "This email's footer" : 'Workspace footer'}
          </p>
          <p className="border-line text-muted shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium">
            {blocks.length} block{blocks.length === 1 ? '' : 's'}
          </p>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={applyStarterTemplate}
              className="btn-ghost h-7 gap-1.5 rounded-lg px-2.5 text-xs"
              title="Insert the footer BlogForger ships, as editable blocks"
            >
              <LayoutTemplate size={12} />
              {blocks.length > 0
                ? 'Replace with system template'
                : 'Start from the system footer template'}
            </button>
            <button
              type="button"
              onClick={() => setTool((t) => (t === 'components' ? 'none' : 'components'))}
              className={`btn-ghost h-7 gap-1.5 rounded-lg px-2.5 text-xs ${
                tool === 'components' ? 'text-accent bg-accent/10' : ''
              }`}
            >
              <ComponentIcon size={12} />
              Insert a saved component
            </button>
            <button
              type="button"
              onClick={() => setTool((t) => (t === 'variables' ? 'none' : 'variables'))}
              className={`btn-ghost h-7 gap-1.5 rounded-lg px-2.5 text-xs ${
                tool === 'variables' ? 'text-accent bg-accent/10' : ''
              }`}
            >
              <Braces size={12} />
              Variables
            </button>
          </div>
        </div>

        {/* ── Empty state — what emails render until a footer is built ── */}
        {blocks.length === 0 && (
          <div className="border-line text-ink-soft bg-card-2/50 flex items-start gap-2.5 border-b px-4 py-2.5 text-[12px]">
            <Info size={14} className="text-accent mt-0.5 shrink-0" />
            <p>
              No footer content yet — emails render the theme's default footer built from your
              footer details. Start from the system template, insert a saved component, or add
              blocks below.
            </p>
          </div>
        )}

        {/* ── Unsubscribe check — the build refuses a footer without one ── */}
        {missingUnsubscribe && (
          <div className="border-warn/40 bg-warn/10 text-warn flex items-start gap-2.5 border-b px-4 py-2.5 text-[12px]">
            <TriangleAlert size={14} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">
                A custom footer must include an unsubscribe link — add{' '}
                <code className="font-mono">{'{{unsubscribe_url}}'}</code> to a link or button. The
                email build fails without it.
              </p>
              {details?.showUnsubscribe === false && (
                <p className="mt-0.5">
                  Your footer details currently switch the unsubscribe link off, which the build
                  tolerates — turn it back on before sending bulk mail.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={addUnsubscribeLine}
              className="border-warn/50 hover:bg-warn/15 shrink-0 rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors"
            >
              Add an unsubscribe line
            </button>
          </div>
        )}

        {/* ── Save failure (422 on an invalid footer, 403 for non-admins) ── */}
        {saveMutation.isError && (
          <div className="border-danger/40 bg-danger/8 text-danger border-b px-4 py-2.5 text-[12px]">
            <p className="font-semibold">
              {saveMutation.error instanceof Error
                ? saveMutation.error.message
                : 'The footer could not be saved.'}
            </p>
            <p className="mt-0.5">
              Nothing was written. Check the blocks for missing required values (an image without a
              URL, for instance); footer blocks must be valid email content.
            </p>
          </div>
        )}

        {tool === 'variables' && (
          <VariablePalette
            copiedToken={copiedToken}
            onInsert={insertVariableLine}
            onCopy={(variable) => void copyToken(variable)}
            onClose={() => setTool('none')}
          />
        )}

        {tool === 'components' && (
          <ComponentPicker
            components={components}
            loading={componentsQuery.isLoading}
            onInsert={insertComponent}
            onClose={() => setTool('none')}
          />
        )}

        {/* ── Canvas ───────────────────────────────────────── */}
        <EditorCanvas contentKind="email" rawVariableDefinitions={variableDefinitions} />
      </div>

      {/* ── Block inspector (styles / per-block settings) ───── */}
      <PanelResizer
        collapsed={inspector.collapsed}
        onResizeStart={inspector.startResize}
        onExpand={() => inspector.setCollapsed(false)}
      />
      {!inspector.collapsed && (
        <>
          {/* Tap-out scrim — only when the inspector overlays the canvas (small screens) */}
          <div
            className="bg-ink/30 fixed inset-0 z-[75] backdrop-blur-[2px] lg:hidden"
            onClick={inspector.toggleCollapsed}
            aria-hidden="true"
          />
          <InspectorPanel
            post={undefined}
            // The footer's variables are the system ones, not an email's.
            variableNameOptions={[...FOOTER_SYSTEM_VARIABLE_NAMES]}
            postId={FOOTER_SCOPE}
            mode="block"
            onToggleCollapse={inspector.toggleCollapsed}
          />
        </>
      )}
    </div>
  );
}
